import dayjs from "dayjs";
import { DOW, DOW_ORDER, MenuType } from "../../../Api/Interface/api.interface";

/** "HH:MM[:SS]" (24h backend) → "h:mm A" for display. */
export const to12h = (hhmm?: string | null): string => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":");
  return dayjs().hour(Number(h)).minute(Number(m || "0")).format("h:mm A");
};

/** dayjs value → "HH:MM" (24h) for the backend. */
export const to24h = (d: dayjs.Dayjs | null): string | null =>
  d && d.isValid() ? d.format("HH:mm") : null;

/** "HH:MM[:SS]" → a dayjs anchored today (for the TimePicker value). */
export const parseTime = (hhmm?: string | null): dayjs.Dayjs | null => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":");
  return dayjs().hour(Number(h)).minute(Number(m || "0")).second(0);
};

/** Minutes since local midnight for "HH:MM[:SS]". */
export const minutesOf = (hhmm?: string | null): number | null => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":");
  return Number(h) * 60 + Number(m || "0");
};

/** Bitmask → array of day indices (0=Mon..6=Sun) that are set. null/0 = all. */
export const daysFromMask = (mask?: number | null): number[] => {
  const m = mask ?? 0;
  if (!m) return [0, 1, 2, 3, 4, 5, 6];
  return DOW_ORDER.map((_, i) => i).filter((i) => (m & (1 << i)) !== 0);
};

/** Toggle a single day index (0=Mon..6=Sun) in a bitmask. */
export const toggleDay = (mask: number, dayIdx: number): number => mask ^ (1 << dayIdx);

/** Whether a day index (0=Mon..6=Sun) is set (null/0 => every day => true). */
export const dayIsSet = (mask: number | null | undefined, dayIdx: number): boolean => {
  const m = mask ?? 0;
  if (!m) return true;
  return (m & (1 << dayIdx)) !== 0;
};

export const MENU_TYPE_LABELS: Record<MenuType, string> = {
  standard_playlist: "Standard Playlist",
  default_menu: "Default Menu",
  daily_special: "Daily Special",
  time_based_menu: "Time-Based Menu",
  holiday_menu: "Holiday Menu",
  promotional_campaign: "Promotional Campaign",
  pos_synced_menu: "POS-Synced Menu",
  emergency_override: "Emergency Announcement",
};

export const MENU_TYPE_HELP: Record<MenuType, string> = {
  standard_playlist: "A normal playlist that plays in sequence.",
  default_menu: "The permanent fallback — plays whenever no scheduled menu is active.",
  daily_special: "Plays on chosen days within a time window (e.g. Monday Specials).",
  time_based_menu: "Switches by time of day (e.g. Breakfast, Lunch, Dinner).",
  holiday_menu: "Plays across a date range then auto-expires (e.g. Christmas).",
  promotional_campaign: "A temporary dated campaign that outranks day/time menus.",
  pos_synced_menu: "Menu imported and kept in sync from your POS.",
  emergency_override: "An urgent takeover shown until it expires.",
};

/** The weekday short labels in Mon..Sun order. */
export const WEEK_DAYS = DOW_ORDER.map((d) => d.short);

/** DOW bit constants re-exported for convenience. */
export { DOW };
